import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import {toast} from 'react-toastify';
import {useQueryClient} from '@tanstack/react-query';
import {RequestParam} from '../../../../../../incase-domain/requestParams';
import {PLAYER_HOST} from '../../../../../../incase-domain/online';
import {
    isSlugConflictError,
    PresentationSlug,
    PresentationSlugParam,
    useCheckSlugAvailable,
    useDeleteSlug,
    useUpdateSlug,
} from '../../../../../../service/hooks/useSlug';
import Button from '../../../../../../incase-domain/components/button/Button';
import ParamPresets from './ParamPresets';
import {hasInvalidRedirectPreset} from '../../../../../../utils/redirect';
import {SLUG_MAX, validateSlugFormat} from './slugValidation';
import {QUERY_KEYS} from '../../../../../../utils/queryKeys';
import {copyToClipboard} from '../../../../../../utils/copyToClipboard';

interface SlugRowProps {
    slug: PresentationSlug;
    presentationId: string;
    requestParams: RequestParam[];
    onDeleted: () => void;
    onUpdated: () => void;
}

function SlugRow({slug, presentationId, requestParams, onDeleted, onUpdated}: SlugRowProps): JSX.Element {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [slugValue, setSlugValue] = useState(slug.slug);
    const [debouncedSlug, setDebouncedSlug] = useState(slug.slug);
    const [params, setParams] = useState<PresentationSlugParam[]>(slug.params ?? []);
    const [formatError, setFormatError] = useState<string | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const {mutate: update, isLoading: isSaving} = useUpdateSlug(slug._id);
    const {mutate: remove, isLoading: isDeleting} = useDeleteSlug();

    const slugChanged = slugValue !== slug.slug;
    const shouldCheck = editing && slugChanged && !formatError && slugValue === debouncedSlug;

    const {data: available, isFetching: checking} = useCheckSlugAvailable(shouldCheck ? debouncedSlug : '', slug._id);

    const shortUrl = `${PLAYER_HOST}/presentation/${slug.slug}`;

    useEffect(() => {
        if (editing) return;
        setSlugValue(slug.slug);
        setDebouncedSlug(slug.slug);
        setParams(slug.params ?? []);
    }, [editing, slug.slug, slug.params]);

    useEffect(() => {
        if (!editing)
            return () => {
                // noop
            };
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebouncedSlug(slugValue), 400);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [slugValue, editing]);

    const handleSave = (): void => {
        if (slugChanged && (formatError || available === false || slugValue !== debouncedSlug)) return;
        update(
            {slug: slugChanged ? slugValue : undefined, params},
            {
                onSuccess: () => {
                    toast.success(t('toasts.successfullySaved'));
                    setEditing(false);
                    queryClient.invalidateQueries(QUERY_KEYS.presentationSlug.byPresentation(presentationId));
                    queryClient.invalidateQueries(QUERY_KEYS.presentationParam.byPresentation(presentationId));
                    onUpdated();
                },
                onError: (error) => {
                    toast.error(t(isSlugConflictError(error) ? 'presEditPage.settings.shortLink.taken' : 'toasts.errorOccurred'));
                },
            }
        );
    };

    const handleDelete = (): void => {
        remove(slug._id, {
            onSuccess: () => {
                toast.success(t('toasts.successfullySaved'));
                queryClient.invalidateQueries(QUERY_KEYS.presentationSlug.byPresentation(presentationId));
                queryClient.invalidateQueries(QUERY_KEYS.presentationParam.byPresentation(presentationId));
                onDeleted();
            },
            onError: () => toast.error(t('toasts.errorOccurred')),
        });
    };

    const handleCopy = (): void => {
        copyToClipboard(shortUrl)
            .then(() => {
                toast.info(t('presEditPage.subNavBar.copiedToClipboard'));
            })
            .catch(() => {
                toast.info(t('presEditPage.subNavBar.failedToCopy'));
            });
    };

    const canSave =
        (!slugChanged || (!formatError && available === true && slugValue === debouncedSlug)) &&
        !checking &&
        !hasInvalidRedirectPreset(params);

    return (
        <div className="c-settingsItem c-slugSettings__item">
            <div className="c-slugSettings__row">
                {editing ? (
                    <>
                        <div className="c-slugSettings__inputWrap">
                            <div className="c-sidepanel__input">
                                <input
                                    type="text"
                                    value={slugValue}
                                    onChange={(e) => {
                                        const val = e.target.value.toLowerCase();
                                        setSlugValue(val);
                                        setFormatError(validateSlugFormat(val));
                                    }}
                                    maxLength={SLUG_MAX}
                                />
                            </div>
                            {checking && (
                                <span className="c-slugSettings__statusIcon">
                                    <Icon icon="solar:refresh-linear" width={14} />
                                </span>
                            )}
                            {!checking && slugChanged && !formatError && available === true && (
                                <span className="c-slugSettings__statusIcon c-slugSettings__statusIcon--success">
                                    <Icon icon="solar:check-circle-linear" width={14} />
                                </span>
                            )}
                            {!checking && slugChanged && (available === false || !!formatError) && (
                                <span className="c-slugSettings__statusIcon c-slugSettings__statusIcon--error">
                                    <Icon icon="solar:close-circle-linear" width={14} />
                                </span>
                            )}
                        </div>
                        <Button type="button" variant="solid" color="info" size="sm" onClick={handleSave} disabled={!canSave || isSaving}>
                            {t('common.save')}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setEditing(false);
                                setSlugValue(slug.slug);
                                setDebouncedSlug(slug.slug);
                                setParams(slug.params ?? []);
                                setFormatError(null);
                            }}
                        >
                            {t('common.cancel')}
                        </Button>
                    </>
                ) : (
                    <>
                        <span className="c-slugSettings__name">{slug.slug}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={handleCopy}>
                            <Icon icon="solar:copy-linear" width={14} />
                        </Button>
                        {slug.isFixed ? (
                            <span className="c-slugSettings__lock" title={t('presEditPage.settings.shortLink.fixedHint')}>
                                <Icon icon="solar:lock-linear" width={14} />
                            </span>
                        ) : (
                            <>
                                <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)}>
                                    <Icon icon="solar:pen-linear" width={14} />
                                </Button>
                                <Button type="button" variant="ghost" color="error" size="sm" onClick={handleDelete} disabled={isDeleting}>
                                    <Icon icon="solar:trash-bin-minimalistic-linear" width={14} />
                                </Button>
                            </>
                        )}
                    </>
                )}
            </div>

            {formatError && slugChanged && <span className="is-error c-slugSettings__hint--error">{formatError}</span>}
            {!formatError && slugChanged && available === false && (
                <span className="is-error c-slugSettings__hint--error">{t('presEditPage.settings.shortLink.taken')}</span>
            )}

            <a href={shortUrl} target="_blank" rel="noreferrer" className="c-slugSettings__url">
                {shortUrl}
            </a>
            {slug.isFixed && <span className="c-slugSettings__hint">{t('presEditPage.settings.shortLink.fixedHint')}</span>}

            {editing && requestParams?.length > 0 && (
                <div className="c-slugSettings__params">
                    <p className="c-slugSettings__paramsTitle">{t('presEditPage.settings.shortLink.params')}</p>
                    <ParamPresets requestParams={requestParams} value={params} onChange={setParams} />
                </div>
            )}

            {!editing && params.length > 0 && (
                <div className="c-slugSettings__params">
                    {params.map((p, i) => (
                        <span key={i} className="c-slugSettings__paramChip">
                            {p.key}={p.value}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

export default SlugRow;
